import { InjectionToken } from '@angular/core';

import { Observable, of } from 'rxjs';

import { retryExponentialBackoffPolicy } from '../../helpers';

/**
 * Service to define http retry policy.
 *
 * @export
 */

export class HttpRequestRetryPolicyService {
    /** @property retryPolicy - holds an observable with attempts to be performed
     */
    retryPolicy: <T>(attempts: Observable<T>) => Observable<number>;

    /**
     * Use Exponential Backoff Policy by default with 3 retries
     * @param numberOfRetries - defines the number of retries that current retry policy will use to try accomplish desired http request successfully
     * @param scaling - defines how often the current retry policy will perform a new retry attempt
     * @param statusCodes - defines which http status codes will be discarded/excluded from current http request's response (ignore some http status codes and retry again)
     */

    constructor(numberOfRetries: number, scaling: number, statusCodes: number[]) {
        this.retryPolicy = this.getExponentialBackoffPolicy(numberOfRetries, scaling, statusCodes);
    }

    /**
     * Get Exponential Backoff Policy
     * @param numberOfRetries - defines the number of retries that current retry policy will use to try accomplish desired http request successfully
     * @param scaling - defines how often the current retry policy will perform a new retry attempt
     * @param statusCodes - defines which http status codes will be discarded/excluded from current http request's response (ignore some http status codes and retry again)
     */
    getExponentialBackoffPolicy(
        numberOfRetries: number,
        scaling: number,
        statusCodes: number[]
    ): <T>(attempts: Observable<T>) => Observable<number> {
        return retryExponentialBackoffPolicy({
            scalingDuration: scaling,
            maxRetryAttempts: numberOfRetries,
            excludedStatusCodes: statusCodes,
        });
    }

    /**
     * Method that provides a 'No Retry Policy'
     */
    noRetryPolicy(): <T>(attempts: Observable<T>) => Observable<number> {
        return this.getExponentialBackoffPolicy(0, 0, []);
    }
}

export const HTTP_REQUEST_RETRY_POLICY_SERVICE_TOKEN_RETRIES = new InjectionToken<number>('request retry policy service number of retries');

export const HTTP_REQUEST_RETRY_POLICY_SERVICE_TOKEN_SCALING = new InjectionToken<number>('request retry policy service scaling duration');

export const HTTP_REQUEST_RETRY_POLICY_SERVICE_TOKEN_STATUS_CODES = new InjectionToken<number[]>(
    'request retry policy service status codes'
);

/**
 * Factory to provide the [HttpRequestRetryPolicyService]{@link HttpRequestRetryPolicyService}
 * @param numberOfRetries - defines the number of retries that current retry policy will use to try accomplish desired http request successfully
 * @param scaling - defines how often the current retry policy will perform a new retry attempt
 * @param statusCodes - defines which http status codes will be discarded/excluded from current http request's response (ignore some http status codes and retry again)
 */
export function PROVIDE_HTTP_REQUEST_RETRY_POLICY_SERVICE_FACTORY(
    numberOfRetries: number,
    scaling: number,
    statusCodes: number[]
): HttpRequestRetryPolicyService {
    return new HttpRequestRetryPolicyService(numberOfRetries, scaling, statusCodes);
}
